/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */
'use client';

import React, { createContext, useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useLanguage } from '@/providers/LanguageProvider';
import { Component } from 'types/api-data';

import { useComponents } from '@/hooks/useComponents';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ErrorComponent } from '@/components/error';

import WorkflowHeadSection from './workflow-details-section/workflow-details-section';

interface CompDetailsHeaderProps {
  workflowName: string;
  children: React.ReactNode;
}

interface LanguageTabs {
  [key: string]: string;
}

export const FindAppContext = createContext<string | undefined>(undefined);

export const CompDetailsHeader = ({
  workflowName,
  children,
}: CompDetailsHeaderProps) => {
  const [associatedApp, setAssociatedApp] = useState('');
  const router = useRouter();
  const pathname = usePathname();
  const {
    state: { error, loading, components },
    componentsActions,
  } = useComponents();
  const { messages } = useLanguage();
  const langData: LanguageTabs = messages?.CompTabs || {};
  const tabs = Object.keys(langData);
  const [activeTab, setActiveTab] = useState(tabs[0]);

  useEffect(() => {
    const newActiveTab =
      tabs.find((tab) => pathname?.includes(`/${tab}`)) || tabs[0];
    setActiveTab(newActiveTab);
  }, [pathname, tabs]);

  const handleTabChange = (value: string) => {
    const url = `/components/${workflowName}${value !== tabs[0] ? `/${value}` : ''}`;
    router.push(url);
  };

  useEffect(() => {
    const findComp = components?.filter(
      (component: Component) => component.component_name === workflowName
    );

    // check if component exists in list
    if (findComp?.length > 0) {
      const findApp =
        findComp[0]?.apps?.length > 0 ? findComp[0].apps[0].name : '';
      setAssociatedApp(findApp);
    }
  }, [components]);

  useEffect(() => {
    if (workflowName) {
      componentsActions.fetchGraphDependencies(workflowName);
      componentsActions.fetchWorkflowDependencies(workflowName);
    }
  }, [workflowName]);

  if (loading || components === null) {
    return (
      <>
        <Skeleton className="animate-pulse-custom mb-3 size-8 w-96 rounded-full bg-[#8C9EA1]" />
        <div className="flex gap-3">
          <Skeleton className="animate-pulse-custom h-[60px] w-44 rounded-xl bg-[#8C9EA1]" />
          <Skeleton className="animate-pulse-custom h-[60px] w-44 rounded-xl bg-[#8C9EA1]" />
          <Skeleton className="animate-pulse-custom h-[60px] w-44 rounded-xl bg-[#8C9EA1]" />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <ErrorComponent status={error.status} statusText={error.statusText} />
    );
  }

  return (
    <>
      <WorkflowHeadSection workflowName={workflowName} />
      <Tabs
        value={activeTab}
        className="w-full"
        onValueChange={handleTabChange}
      >
        <TabsList className="h-[48px] w-full justify-start space-x-8 rounded-none border-b border-[#40565A] bg-transparent p-0">
          {tabs.map((tab) => (
            <TabsTrigger
              key={tab}
              value={tab}
              className="px-0 text-base font-medium text-link-active decoration-[3px] underline-offset-[16px] hover:underline data-[state=active]:bg-transparent data-[state=active]:underline"
            >
              {langData[tab]}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={activeTab}>
          <FindAppContext.Provider value={associatedApp}>
            {children}
          </FindAppContext.Provider>
        </TabsContent>
      </Tabs>
    </>
  );
};
